import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { formatFeeDisplay } from '../utils/formatters';
import { getCompare } from '../utils/compare';

function useQuery() {
 return new URLSearchParams(useLocation().search);
}

import useDocumentMetadata from '../hooks/useDocumentMetadata';

const ComparePage = () => {
  useDocumentMetadata(
    'Compare Universities & Programs - Dakhla',
    'Compare fee structures, eligibility criteria, admission deadlines, and campus facilities across top Pakistani universities.'
  );

  const query = useQuery();
  const idsParam = query.get('ids');
 const [programs, setPrograms] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const navigate = useNavigate();

 useEffect(() => {
 const stored = getCompare();

 // If no IDs in URL but we have stored items, navigate with IDs (include type)
 if ((!idsParam || idsParam.trim() === '') && stored && stored.length > 0) {
 const ids = stored.map(i => `${i.id}:${i.type || 'partner'}`).join(',');
 navigate(`/compare?ids=${ids}`, { replace: true });
 return;
 }

 // Only fetch if we have IDs
 if (!idsParam || idsParam.trim() === '') {
 setLoading(false);
 return;
 }

 const fetchPrograms = async () => {
 setLoading(true);
 setError(null);
 try {
 
 const url = `${API_BASE_URL}/programs?ids=${encodeURIComponent(idsParam)}`;
 const resp = await fetch(url);
 
 if (!resp.ok) {
 const text = await resp.text();
 throw new Error(`API returned ${resp.status}: ${text.substring(0, 200)}`);
 }
 
 const data = await resp.json();
 setPrograms(Array.isArray(data) ? data : []);
 } catch (e) {
 console.error('[ComparePage] Error fetching programs:', e);
 setError(e.message);
 setPrograms([]);
 } finally {
 setLoading(false);
 }
 };

 fetchPrograms();
 }, [idsParam, navigate]);

 if (loading) return (
 <div className="min-h-screen bg-slate-100 flex items-center justify-center">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
 <p className="text-slate-600 font-semibold text-sm">Loading comparison details...</p>
 </div>
 </div>
 );

 if (error) return (
 <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
 <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-6 shadow-xl text-center">
 <h3 className="text-lg font-bold text-slate-900 mb-2">Error Loading Comparison</h3>
 <p className="text-slate-600 text-sm mb-6">{error}</p>
 <button 
 onClick={() => navigate(-1)} 
 className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-2.5 rounded-xl text-sm transition-all shadow-md shadow-primary-500/10"
 >
 Go Back
 </button>
 </div>
 </div>
 );

 if (!programs || programs.length === 0) return (
 <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
 <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-6 shadow-xl text-center">
 <h3 className="text-lg font-bold text-slate-900 mb-2">No Programs Selected</h3>
 <p className="text-slate-500 text-sm mb-6">Select up to 4 programs from university detail pages to compare them here.</p>
 <button 
 onClick={() => navigate(-1)} 
 className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-2.5 rounded-xl text-sm transition-all shadow-md shadow-primary-500/10"
 >
 Go Back
 </button>
 </div>
 </div>
 );

 const normalizeWebsite = (url) => {
 if (!url) return '#';
 const trimmed = String(url).trim();
 if (/^https?:\/\//i.test(trimmed)) return trimmed;
 if (/\./.test(trimmed)) return `https://${trimmed.replace(/^\/+/, '')}`;
 return trimmed;
 };

 return (
 <div className="min-h-screen bg-slate-100 pt-12 pb-36">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 
 {/* Header Block */}
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
 <div>
 <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Program Comparison</h1>
 <p className="text-slate-500 mt-1.5 text-sm">Compare selected programs side-by-side.</p>
 </div>
 <button 
 onClick={() => navigate(-1)} 
 className="flex items-center gap-1.5 bg-white hover:bg-slate-50 border border-slate-200 shadow-sm rounded-xl px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 transition-colors w-fit"
 >
 ← Go Back
 </button>
 </div>

 {/* Desktop View: Clean styled Table Container */}
 <div className="hidden md:block overflow-x-auto bg-white rounded-2xl border border-slate-300 shadow-lg p-5">
 <table className="min-w-full divide-y divide-slate-200">
 <thead>
 <tr className="bg-slate-50">
 <th className="py-2.5 px-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider w-48 align-middle">Field</th>
 {programs.map(p => (
 <th key={p.id} className="py-2.5 px-4 text-left align-top text-sm font-bold text-slate-700 border-l border-slate-200">
 <div className="flex items-center gap-3 mb-2">
 {p.university?.profile_picture && (
 <img 
 src={p.university.profile_picture} 
 alt={p.university.name} 
 className="w-10 h-10 object-contain rounded-lg border border-slate-200 bg-white p-1 shadow-sm" 
 />
 )}
 <div className="text-xs">
 <div className="font-extrabold text-slate-800 break-words max-w-[14rem]">{p.university?.name || 'N/A'}</div>
 <div className="text-[10px] text-slate-400 font-medium break-words max-w-[14rem] mt-0.5">{p.university?.location || ''}</div>
 </div>
 </div>
 <div className="mt-2 text-sm font-extrabold text-primary-700 break-words">{p.program_name}</div>
 </th>
 ))}
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-200">
 
              <tr>
                <td className="py-2 px-4 text-xs font-bold text-slate-400 uppercase tracking-wider align-middle">Tuition Fee</td>
                {programs.map(p => (
                  <td key={p.id} className="py-2 px-4 text-sm font-semibold text-slate-700 border-l border-slate-200">
                    {formatFeeDisplay(p.program_fee, p.fee_type || 'semester')}
                  </td>
                ))}
              </tr>

 <tr>
 <td className="py-2 px-4 text-xs font-bold text-slate-400 uppercase tracking-wider align-middle">Admission Deadline</td>
 {programs.map(p => (
 <td key={p.id} className="py-2 px-4 text-sm font-semibold text-slate-700 border-l border-slate-200">{p.application_deadline || 'N/A'}</td>
 ))}
 </tr>

 <tr>
 <td className="py-2 px-4 text-xs font-bold text-slate-400 uppercase tracking-wider align-middle">Merit Cutoff</td>
 {programs.map(p => (
 <td key={p.id} className="py-2 px-4 text-sm font-semibold text-slate-700 border-l border-slate-200">
 {(() => {
 const min = p.last_year_cutoff_min;
 const max = p.last_year_cutoff_max;
 if (min != null && max != null) return `${min}% - ${max}%`;
 if (min != null) return `${min}%`;
 if (max != null) return `${max}%`;
 return '-';
 })()}
 </td>
 ))}
 </tr>

 <tr>
 <td className="py-2 px-4 text-xs font-bold text-slate-400 uppercase tracking-wider align-middle">Duration</td>
 {programs.map(p => (
 <td key={p.id} className="py-2 px-4 text-sm font-semibold text-slate-700 border-l border-slate-200">{p.duration || '-'}</td>
 ))}
 </tr>

 <tr>
 <td className="py-2 px-4 text-xs font-bold text-slate-400 uppercase tracking-wider align-middle">Degree Type</td>
 {programs.map(p => (
 <td key={p.id} className="py-2 px-4 text-sm font-semibold text-slate-700 border-l border-slate-200">{p.degree_type || '-'}</td>
 ))}
 </tr>

 <tr>
 <td className="py-2.5 px-4 text-xs font-bold text-slate-400 uppercase tracking-wider align-middle">Actions</td>
 {programs.map(p => (
 <td key={p.id} className="py-2.5 px-4 text-sm border-l border-slate-200">
 <div className="flex flex-wrap gap-2">
 <a
 href={`/university/${p.university?.id}${p.university && !p.university.is_partner ? '?type=static' : ''}`}
 className="px-3.5 py-2 border border-primary-600 text-primary-600 rounded-xl text-xs font-bold hover:bg-primary-50 transition-colors shadow-sm shrink-0"
 >
 Visit University
 </a>

 {p.university?.is_partner ? (
 <a 
 href={`/application-form?program_id=${p.id}&university_id=${p.university?.id}`} 
 className="px-3.5 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-emerald-500/10 hover:shadow-emerald-500/20 shrink-0"
 >
 Apply Now
 </a>
 ) : (
 p.university?.website ? (
 <a 
 href={normalizeWebsite(p.university.website)} 
 target="_blank" 
 rel="noopener noreferrer" 
 className="px-3.5 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition-colors shadow-sm shrink-0"
 >
 Visit Website
 </a>
 ) : null
 )}
 </div>
 </td>
 ))}
 </tr>
 </tbody>
 </table>
 </div>

 {/* Mobile View: Cards Container */}
 <div className="md:hidden flex flex-col gap-6">
 {programs.map(p => (
 <div key={p.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
 <div className="p-5 border-b border-slate-100 bg-slate-50 flex flex-col gap-3">
 <div className="flex items-center gap-3">
 {p.university?.profile_picture ? (
 <img 
 src={p.university.profile_picture} 
 alt={p.university.name} 
 className="w-12 h-12 object-contain rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm shrink-0" 
 />
 ) : (
 <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center border border-primary-100/30 text-primary-600 font-bold">
 {(p.university?.name || 'U').charAt(0)}
 </div>
 )}
 <div>
 <h3 className="font-extrabold text-slate-800 leading-tight">{p.university?.name || 'N/A'}</h3>
 <p className="text-xs text-slate-500 font-medium mt-0.5">{p.university?.location || ''}</p>
 </div>
 </div>
 <div className="pt-2">
 <h4 className="text-[15px] font-extrabold text-primary-700 leading-tight">{p.program_name}</h4>
 </div>
 </div>
 
 <div className="p-5 flex flex-col gap-4">
 <div className="flex justify-between items-start gap-4 border-b border-slate-100 pb-3">
 <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-0.5">Tuition Fee</span>
 <span className="text-sm font-semibold text-slate-800 text-right">{formatFeeDisplay(p.program_fee, p.fee_type || 'semester')}</span>
 </div>
 
 <div className="flex justify-between items-start gap-4 border-b border-slate-100 pb-3">
 <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-0.5">Deadline</span>
 <span className="text-sm font-semibold text-slate-800 text-right">{p.application_deadline || 'N/A'}</span>
 </div>
 
 <div className="flex justify-between items-start gap-4 border-b border-slate-100 pb-3">
 <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-0.5">Merit Cutoff</span>
 <span className="text-sm font-semibold text-slate-800 text-right">
 {(() => {
 const min = p.last_year_cutoff_min;
 const max = p.last_year_cutoff_max;
 if (min != null && max != null) return `${min}% - ${max}%`;
 if (min != null) return `${min}%`;
 if (max != null) return `${max}%`;
 return '-';
 })()}
 </span>
 </div>
 
 <div className="flex justify-between items-start gap-4 border-b border-slate-100 pb-3">
 <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-0.5">Duration</span>
 <span className="text-sm font-semibold text-slate-800 text-right">{p.duration || '-'}</span>
 </div>
 
 <div className="flex justify-between items-start gap-4">
 <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-0.5">Degree Type</span>
 <span className="text-sm font-semibold text-slate-800 text-right">{p.degree_type || '-'}</span>
 </div>
 </div>
 
 <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col gap-2.5">
 <a
 href={`/university/${p.university?.id}${p.university && !p.university.is_partner ? '?type=static' : ''}`}
 className="w-full flex items-center justify-center px-4 py-2.5 border-2 border-primary-600 text-primary-700 rounded-xl text-sm font-bold hover:bg-primary-50 transition-colors bg-white shadow-sm"
 >
 Visit University
 </a>
 
 {p.university?.is_partner ? (
 <a 
 href={`/application-form?program_id=${p.id}&university_id=${p.university?.id}`} 
 className="w-full flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl text-sm font-bold shadow-md shadow-emerald-500/10"
 >
 Apply Now
 </a>
 ) : p.university?.website ? (
 <a 
 href={normalizeWebsite(p.university.website)} 
 target="_blank" 
 rel="noopener noreferrer" 
 className="w-full flex items-center justify-center px-4 py-2.5 border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-xl text-sm font-bold shadow-sm transition-colors"
 >
 Visit Website
 </a>
 ) : null}
 </div>
 </div>
 ))}
 </div>
 </div>
 </div>
 );
};

export default ComparePage;
